import { PenLine, Trash2, Upload, UploadCloud, X } from "lucide-react";
import {
  createContext,
  type FC,
  type JSX,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  UseFormStateReturn,
} from "react-hook-form";
import ImageUploadingModule, {
  type ErrorsType,
  type ImageListType,
  type ImageType,
} from "react-images-uploading";
import { ExportInterface } from "react-images-uploading/dist/typings";
import { defineMessages, useIntl } from "react-intl";

import FormWrapper from "../customFormFields/FormWrapper";
import Avatar from "../displayPrimitives/Avatar";
import { fileToBase64 } from "../minimalTiptap/utils";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { ScrollArea } from "../ui/ScrollArea";
import { Skeleton } from "../ui/skeleton";

import {
  ONE_MB,
  allowedImageTypes,
  imgBaseUrl,
} from "@/app/constants/constants";
import placeholderImg from "@/assets/images/empty.jpg";
import pdfImg from "@/assets/images/icons/pdf.svg";
import { customToast } from "@/components/alerts/CustomToast";
import { FileImagePreview } from "@/components/imagePreview/FilePreviewUtils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import {
  convertImageFormat,
  isHeicFile,
  splitImageFileName,
} from "@/utils/content/fileImage.utils";
import { cn, formatFileSize, formatSizeToKB } from "@/utils/core/generalUtils";
import { resolveDefaultExport } from "@/utils/core/moduleInterop";
import { isHttps } from "@/utils/core/urlHelper";

const ImageUploading = resolveDefaultExport(ImageUploadingModule);

const fileUploadMessages = defineMessages({
  addMoreFileQuestion: {
    id: "components.fileUpload.addMoreFileQuestion",
    defaultMessage: "Add More File?",
  },
  addMorePhotoQuestion: {
    id: "components.fileUpload.addMorePhotoQuestion",
    defaultMessage: "Add More Photo?",
  },
  dropClickUploadPlaceholder: {
    id: "components.fileUpload.dropClickUploadPlaceholder",
    defaultMessage: "Drop/Click here to Upload",
  },
  editAction: {
    id: "components.fileUpload.editAction",
    defaultMessage: "Edit",
  },
  fileSizeShouldNotExceed: {
    id: "components.fileUpload.fileSizeShouldNotExceed",
    defaultMessage: "File size should not exceed {maxMb} MB",
  },
  formatConversionFailed: {
    id: "components.fileUpload.formatConversionFailed",
    defaultMessage: "Failed to convert image",
  },
  invalidFileType: {
    id: "components.fileUpload.invalidFileType",
    defaultMessage: "invalid file type",
  },
  moreFilesTitle: {
    id: "components.fileUpload.moreFilesTitle",
    defaultMessage: "{count} more {count, plural, one {file} other {files}}",
  },
  removeAction: {
    id: "components.fileUpload.removeAction",
    defaultMessage: "Remove",
  },
  removeImageSrOnly: {
    id: "components.fileUpload.removeImageSrOnly",
    defaultMessage: "remove image",
  },
  removeSrOnly: {
    id: "components.fileUpload.removeSrOnly",
    defaultMessage: "remove",
  },
  selectedFilesTitle: {
    id: "components.fileUpload.selectedFilesTitle",
    defaultMessage: "Selected Files",
  },
  selectedImagesTitle: {
    id: "components.fileUpload.selectedImagesTitle",
    defaultMessage: "Selected Images",
  },
  uploadAction: {
    id: "components.fileUpload.uploadAction",
    defaultMessage: "Upload",
  },
  uploadLimitError: {
    id: "components.fileUpload.uploadLimitError",
    defaultMessage: "You can upload up to {maxNumber} files.",
  },
});

export const getPreviewImage = ({ data }: { data: ImageType }): string => {
  const { dataURL = "", file } = data;

  if (dataURL && !file) {
    // Check if dataURL corresponds to a PDF
    return dataURL.includes(".pdf") ? pdfImg : dataURL;
  }

  if (file) {
    const { type = "" } = file;

    // Determine preview based on file type
    if (type.includes("image")) {
      return dataURL || placeholderImg;
    }

    if (type.includes("application")) {
      return pdfImg;
    }
  }

  // Default fallback
  return placeholderImg;
};

type ExtendedImageListType = ImageListType & {
  name?: string;
  size?: number;
};

type ExtendedImageType = ImageType & {
  name?: string;
  size?: number;
};

const getFileBatchSignature = (
  files: File[] | undefined,
  batchKey: number | undefined,
): string => {
  if (!files?.length) {
    return "";
  }

  return [
    batchKey ?? "initial",
    files
      .map((file) => `${file.name}:${file.size}:${file.lastModified}`)
      .join("|"),
  ].join(":");
};

type UploadFieldValue = string | File | undefined;

const isSameFile = (currentFile: File, nextFile: File): boolean => {
  return (
    currentFile.name === nextFile.name &&
    currentFile.size === nextFile.size &&
    currentFile.type === nextFile.type &&
    currentFile.lastModified === nextFile.lastModified
  );
};

const areUploadFieldValuesEqual = (
  currentFieldValue: unknown,
  nextFieldValues: UploadFieldValue[],
): boolean => {
  if (!Array.isArray(currentFieldValue)) {
    return false;
  }

  if (currentFieldValue.length !== nextFieldValues.length) {
    return false;
  }

  return currentFieldValue.every((currentFieldFile, fieldIndex) => {
    const nextFieldFile = nextFieldValues[fieldIndex];

    if (currentFieldFile instanceof File && nextFieldFile instanceof File) {
      return isSameFile(currentFieldFile, nextFieldFile);
    }

    return currentFieldFile === nextFieldFile;
  });
};

type FileContextType = {
  images: ExtendedImageListType;
  setImages: (images: ExtendedImageListType) => void;
  isForFileUpload: boolean;
  setIsForFileUpload: (isForFileUpload: boolean) => void;
  acceptFileTypes: string[];
  setAcceptFileTypes: (fileTypes: string[]) => void;
  isConverting: boolean;
  setIsConverting: (isConverting: boolean) => void;
  isAllowVideoUpload: boolean;
  setIsAllowVideoUpload: (isAllowVideoUpload: boolean) => void;
  onChange: (
    imageList: ImageListType,
    addUpdatedIndex?: number[],
    isUploadedFromParent?: boolean,
  ) => Promise<void>;
};

const FileContext = createContext<FileContextType | undefined>(undefined);

export const useFileContext = (): FileContextType => {
  const context = useContext(FileContext);
  if (!context) {
    throw new Error("useFileContext must be used within a FileProvider");
  }

  return context;
};

export const FileProvider: FC<{
  children: ReactNode;
  excludeFileLimit?: boolean;
  fileLimit?: number;
}> = ({ children, excludeFileLimit = false, fileLimit = 2 * ONE_MB }) => {
  const intl = useIntl();
  const [images, setImages] = useState<ExtendedImageListType>([]);
  const imagesRef = useRef<ExtendedImageListType>(images);
  useEffect(() => {
    imagesRef.current = images;
  }, [images]);
  const [isForFileUpload, setIsForFileUpload] = useState<boolean>(false);
  const [isAllowVideoUpload, setIsAllowVideoUpload] = useState<boolean>(false);
  const [acceptFileTypes, setAcceptFileTypes] = useState<string[]>([]);
  const [isConverting, setIsConverting] = useState<boolean>(false);

  const hasSupportedFormat = useCallback(
    async (file: File | undefined): Promise<boolean> => {
      if (!file) {
        return false;
      }

      const isHeicImage = await isHeicFile(file);

      return isHeicImage;
    },
    [],
  );

  const isValidFileType = useCallback(
    (file: File | undefined): boolean =>
      file ? acceptFileTypes.includes(file.type.split("/")[1]) : false,
    [acceptFileTypes],
  );

  const isFileSizeValid = useCallback(
    (file: File | undefined): boolean =>
      excludeFileLimit || !file || file.size <= fileLimit,
    [excludeFileLimit, fileLimit],
  );

  const processImage = useCallback(
    async (
      image: ImageType,
      isUploadedFromParent: boolean = false,
    ): Promise<ExtendedImageType | null> => {
      // Handle HTTPS images
      if (image?.dataURL && isHttps(image.dataURL)) {
        return {
          ...image,
          name: splitImageFileName(image.dataURL, imgBaseUrl),
          size: 0,
        };
      }

      // Validation for parent uploads
      if (isUploadedFromParent && image.file && !isValidFileType(image.file)) {
        customToast.error(
          `${image.file.name} ${intl.formatMessage(fileUploadMessages.invalidFileType)}`,
        );

        return null;
      }

      if (image.file && !isFileSizeValid(image.file)) {
        customToast.error(
          intl.formatMessage(fileUploadMessages.fileSizeShouldNotExceed, {
            maxMb: fileLimit / ONE_MB,
          }),
        );

        return null;
      }
      const isHeicImage = await hasSupportedFormat(image.file);
      if (image?.file && isHeicImage) {
        try {
          const tempFile = await convertImageFormat({
            file: image.file,
            targetFormat: "webp",
          });

          if (!tempFile) {
            customToast.error(
              intl.formatMessage(fileUploadMessages.formatConversionFailed),
            );

            return null;
          }
          const convertedFileInBase64 = await fileToBase64(tempFile);

          return {
            ...image,
            dataURL: convertedFileInBase64,
            file: tempFile,
            name: image?.file?.name,
            size: tempFile?.size,
          };
        } catch (error) {
          return null;
        }
      }

      return {
        ...image,
        file: image.file,
        name: image.file?.name,
        size: image.file?.size,
      };
    },
    [fileLimit, hasSupportedFormat, intl, isFileSizeValid, isValidFileType],
  );

  const onChange = useCallback(
    async (
      imageList: ImageListType,
      _addUpdatedIndex?: number[],
      isUploadedFromParent?: boolean,
    ): Promise<void> => {
      if (imageList.length === 0) {
        setImages([
          {
            dataURL: undefined,
            file: undefined,
            name: undefined,
            size: undefined,
          },
        ]);

        return;
      }

      // If the new imageList is shorter than current images, it's a deletion
      const currentValidImages = imagesRef.current.filter(
        (img) => img.file || img.dataURL,
      );
      if (imageList.length < currentValidImages.length) {
        setImages(imageList.filter((image) => image !== null));

        return;
      }

      // Check if conversion is needed for any newly added images
      const needsConversion = (
        await Promise.all(
          imageList.map(
            (image) =>
              image.file &&
              !(image as ExtendedImageType).name &&
              hasSupportedFormat(image.file),
          ),
        )
      ).some(Boolean);

      if (needsConversion) {
        setIsConverting(true);
      }

      try {
        const processedImages = await Promise.all(
          imageList.map((image) => {
            // If already processed (has name/size), return as is to avoid re-processing
            if ((image as ExtendedImageType).name) {
              return image as ExtendedImageType;
            }

            return processImage(image, isUploadedFromParent);
          }),
        );

        const validImages = processedImages.filter(
          (image): image is ExtendedImageType =>
            image !== null && image.name !== undefined,
        );

        setImages(() => {
          // Use a Map to efficiently deduplicate images based on a composite key.
          const uniqueImagesMap = new Map<string, ExtendedImageType>();
          for (const image of validImages) {
            const key = `${image.file?.name}|${image.file?.size}|${image.dataURL}`;
            if (!uniqueImagesMap.has(key)) {
              uniqueImagesMap.set(key, image);
            }
          }

          return Array.from(uniqueImagesMap.values());
        });
      } catch (error) {
        customToast.error(
          intl.formatMessage(fileUploadMessages.formatConversionFailed),
        );
      } finally {
        if (needsConversion) {
          setIsConverting(false);
        }
      }
    },
    [hasSupportedFormat, intl, processImage],
  );

  return (
    <FileContext.Provider
      value={{
        images,
        setImages,
        isForFileUpload,
        setIsForFileUpload,
        acceptFileTypes,
        setAcceptFileTypes,
        isConverting,
        setIsConverting,
        isAllowVideoUpload,
        setIsAllowVideoUpload,
        onChange,
      }}
    >
      {children}
    </FileContext.Provider>
  );
};

const LoadingOverlay: FC = () => (
  <div className='absolute inset-0 rounded-md flex items-center z-10 px-1 gap-2 bg-background'>
    <Skeleton className='h-9 w-10' />
    <Skeleton className='h-9 w-full' />
  </div>
);

interface FileProps {
  onImageUpload: () => void;
  className?: string;
  isForVideoUpload?: boolean;
  disabled?: boolean;
}
const FileInput = ({
  onImageUpload,
  className,
  isForVideoUpload,
  disabled,
  ...props
}: FileProps): JSX.Element => {
  const intl = useIntl();
  let accept = "";
  if (isForVideoUpload) {
    accept += (accept ? ", " : "") + "video/*";
  }
  accept = accept || "application/*";

  return (
    <div
      className={cn(
        `flex justify-between border border-input p-1 max-h-11 rounded-md shadow-sm`,
        className,
      )}
    >
      <div className='flex flex-1'>
        <Input
          icon={{
            icon: UploadCloud,
            position: "left",
          }}
          accept={accept}
          className={cn(
            "border-0 h-8 w-full cursor-pointer active:border-0 focus:border-0 ring-0 text-xs focus-visible:ring-transparent shadow-none",
          )}
          onClick={onImageUpload}
          placeholder={intl.formatMessage(
            fileUploadMessages.dropClickUploadPlaceholder,
          )}
          readOnly
          disabled={disabled}
          {...props}
        />
      </div>
      <Button
        variant={"secondary"}
        className='h-8 py-0 text-aldgray'
        onClick={onImageUpload}
        disabled={disabled}
      >
        <Upload size={18} className='mr-2' />
        {intl.formatMessage(fileUploadMessages.uploadAction)}
        <span className='sr-only'>
          {intl.formatMessage(fileUploadMessages.uploadAction)}
        </span>
      </Button>
    </div>
  );
};

type MultipleImagePreviewProps = Pick<ExportInterface, "dragProps"> & {
  imageList: ExtendedImageListType;
  onImageRemove: (index: number) => void;
  onImageUpload: () => void;
  onRemove?: (url: string) => void;
  maxFilePreview?: number;
  disabled?: boolean;
};

const MultipleImagePreview: FC<MultipleImagePreviewProps> = ({
  imageList,
  onImageRemove,
  onImageUpload,
  onRemove,
  maxFilePreview,
  dragProps,
  disabled,
}) => {
  const intl = useIntl();
  const { isForFileUpload, isAllowVideoUpload } = useFileContext();
  const title = isForFileUpload
    ? intl.formatMessage(fileUploadMessages.selectedFilesTitle)
    : intl.formatMessage(fileUploadMessages.selectedImagesTitle);
  const subTitle = isForFileUpload
    ? intl.formatMessage(fileUploadMessages.addMoreFileQuestion)
    : intl.formatMessage(fileUploadMessages.addMorePhotoQuestion);
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(() => {
    if (typeof maxFilePreview === "number") {
      return Math.min(maxFilePreview, imageList.length);
    }

    return Math.min(4, imageList.length);
  });
  const ICON_SIZE = 40;
  const ICON_GAP = 4;

  const updateVisibleCount = useCallback(() => {
    const containerWidth = previewContainerRef.current?.clientWidth ?? 0;
    if (!containerWidth) {
      const fallbackCount =
        typeof maxFilePreview === "number"
          ? Math.min(maxFilePreview, imageList.length)
          : Math.min(4, imageList.length);
      setVisibleCount((prev) =>
        prev === fallbackCount ? prev : fallbackCount,
      );

      return;
    }

    const capacity = Math.max(
      1,
      Math.floor((containerWidth + ICON_GAP) / (ICON_SIZE + ICON_GAP)),
    );
    const cappedCapacity =
      typeof maxFilePreview === "number"
        ? Math.min(maxFilePreview, capacity)
        : capacity;
    const hasOverflow = imageList.length > cappedCapacity;
    const nextVisibleCount = hasOverflow
      ? Math.max(0, cappedCapacity - 1)
      : Math.min(cappedCapacity, imageList.length);

    setVisibleCount((prev) =>
      prev === nextVisibleCount ? prev : nextVisibleCount,
    );
  }, [imageList.length, maxFilePreview]);

  useEffect(() => {
    updateVisibleCount();
  }, [updateVisibleCount]);

  useEffect((): (() => void) | undefined => {
    if (!previewContainerRef.current || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const observer = new ResizeObserver(() => updateVisibleCount());
    observer.observe(previewContainerRef.current);

    return (): void => observer.disconnect();
  }, [updateVisibleCount]);

  const maxPreview = imageList.slice(0, visibleCount);
  const overflowCount = Math.max(0, imageList.length - visibleCount);

  return (
    <div
      className={cn(
        "flex gap-2 justify-between w-full items-center border border-input p-[2px] rounded-md shadow-sm",
      )}
      {...dragProps}
    >
      <div
        className={cn("flex flex-1 min-w-0 gap-1")}
        ref={previewContainerRef}
      >
        {maxPreview?.map((file, index) => {
          return (
            <div key={index} className='flex shrink-0'>
              <Avatar
                src={getPreviewImage({
                  data: file,
                })}
                name=''
                titleName={file?.name}
              />
            </div>
          );
        })}
        {overflowCount > 0 && (
          <div
            className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-dashed border-input text-xs font-semibold text-muted-foreground'
            title={intl.formatMessage(fileUploadMessages.moreFilesTitle, {
              count: overflowCount,
            })}
          >
            +{overflowCount}
          </div>
        )}
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant={"secondary"} className='py-0 h-9 '>
            <PenLine className='w-4 h-4 mr-2 ' />
            {intl.formatMessage(fileUploadMessages.editAction)}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className='mt-1 space-y-2.5'
          align='end'
          {...dragProps}
        >
          <DropdownMenuGroup className='px-2 space-y-2'>
            <DropdownMenuLabel className='px-0 pb-0'>{title}</DropdownMenuLabel>
            <ScrollArea className='max-h-72 scrollbar-thin'>
              {imageList?.map((file, index) => {
                return (
                  <DropdownMenuItem
                    className='border border-input !hover:bg-transparent focus:bg-transparent my-2'
                    key={index}
                  >
                    <div className='flex items-center flex-1 gap-2'>
                      <Avatar
                        src={getPreviewImage({
                          data: file,
                        })}
                        name=''
                        titleName={file?.name}
                      />
                      <div className='min-w-0 flex-1'>
                        <h2
                          className='text-xs font-semibold truncate'
                          title={file.name}
                        >
                          {file.name}
                        </h2>
                        <p className=' text-xs  text-gray-400'>
                          {formatSizeToKB(file?.size)}
                        </p>
                      </div>
                    </div>
                    <Button
                      className='text-red-800 bg-transparent hover:bg-[#F9E0E0] h-8 px-1 '
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (file?.dataURL && isHttps(file.dataURL)) {
                          if (onRemove) {
                            onRemove(file.dataURL);
                          }
                        }
                        onImageRemove(index);
                      }}
                    >
                      <Trash2 size={18} />
                      <span className='sr-only'>
                        {intl.formatMessage(
                          fileUploadMessages.removeImageSrOnly,
                        )}
                      </span>
                    </Button>
                  </DropdownMenuItem>
                );
              })}
            </ScrollArea>
          </DropdownMenuGroup>
          <DropdownMenuGroup className='px-2 space-y-2 '>
            <DropdownMenuLabel className='px-0 pb-0'>
              {subTitle}
            </DropdownMenuLabel>
            <FileInput
              className='p-1 mb-2 border '
              onImageUpload={onImageUpload}
              isForVideoUpload={isAllowVideoUpload}
              disabled={disabled}
            />
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

type FileInputWrapperProps = {
  isMultiple?: boolean;
  maxFiles?: number;
  className?: string;
  onRemove?: (url: string) => void;
  maxFilePreview?: number;
  fileLimit?: number;
  excludeFileLimit?: boolean;
  filesUploadedFromParent?: File[];
  filesUploadedBatchKey?: number;
  disabled?: boolean;
};

export const FileInputWrapper: FC<FileInputWrapperProps> = ({
  isMultiple = false,
  maxFiles = 69,
  className,
  onRemove,
  maxFilePreview,
  filesUploadedFromParent,
  filesUploadedBatchKey,
  disabled,
}) => {
  const intl = useIntl();
  const { images, isForFileUpload, acceptFileTypes, isConverting, onChange } =
    useFileContext();
  const imagesRef = useRef(images);
  const processedFileBatchSignatureRef = useRef("");
  const parentFileBatchSignature = useMemo(
    () => getFileBatchSignature(filesUploadedFromParent, filesUploadedBatchKey),
    [filesUploadedBatchKey, filesUploadedFromParent],
  );

  const maxNumber = isMultiple ? maxFiles : 1;
  const handleUploadError = useCallback(
    (errors: ErrorsType) => {
      if (!errors) {
        return;
      }

      if (errors.maxNumber) {
        customToast.error(
          intl.formatMessage(fileUploadMessages.uploadLimitError, {
            maxNumber,
          }),
        );
      }
    },
    [intl, maxNumber],
  );

  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  useEffect((): (() => void) | undefined => {
    if (!filesUploadedFromParent?.length) {
      processedFileBatchSignatureRef.current = "";

      return undefined;
    }

    if (parentFileBatchSignature === processedFileBatchSignatureRef.current) {
      return undefined;
    }

    processedFileBatchSignatureRef.current = parentFileBatchSignature;
    let isActive = true;

    const processFiles = async (): Promise<void> => {
      const newlyUploadedFiles = await Promise.all(
        filesUploadedFromParent.map(async (file) => {
          const dataURL = await fileToBase64(file);

          return {
            dataURL,
            file,
          };
        }),
      );

      if (!isActive) {
        return;
      }

      void onChange(
        [...imagesRef.current, ...newlyUploadedFiles],
        undefined,
        true,
      );
    };

    void processFiles();

    return () => {
      isActive = false;
    };
  }, [filesUploadedFromParent, onChange, parentFileBatchSignature]);

  return (
    <div className='relative'>
      {isConverting && <LoadingOverlay />}
      <ImageUploading
        multiple={isMultiple}
        value={images}
        onChange={onChange}
        maxNumber={maxNumber}
        acceptType={acceptFileTypes ?? allowedImageTypes}
        allowNonImageType={isForFileUpload}
        onError={handleUploadError}
      >
        {({ onImageUpload, onImageRemove, dragProps }): JSX.Element => {
          if (images?.length > 0 && isMultiple) {
            return (
              <MultipleImagePreview
                imageList={images}
                onImageRemove={onImageRemove}
                onImageUpload={onImageUpload}
                onRemove={onRemove}
                maxFilePreview={maxFilePreview}
                dragProps={dragProps}
                disabled={disabled}
              />
            );
          } else if (images?.length > 0 && !isMultiple) {
            const getExtension = (
              image: File | undefined,
            ): string | undefined => {
              if (!(image instanceof File) || !image) {
                return undefined;
              }
              const imageType = image.type;

              if (!imageType || !imageType.includes("/")) {
                return undefined;
              }

              const [, extension] = imageType.split("/");

              return extension;
            };

            return (
              <div className='grid grid-cols-3 justify-center  items-center w-full border border-input px-[2px] py-[1px]  max-h-10 rounded-md shadow-sm'>
                <div className='flex items-center col-span-2'>
                  {FileImagePreview({
                    file: images[0]?.dataURL ?? "",
                    className: " rounded-sm !size-7 mb-1.5",
                    extension: getExtension(images[0]?.file),
                  })}

                  <div className='flex min-w-0 flex-1 flex-col justify-center ml-2'>
                    <p
                      className='text-xs truncate text-aldgray'
                      title={images[0]?.name}
                    >
                      {images[0]?.name}
                    </p>
                    <p className='text-xs text-aldgray'>
                      {formatFileSize(images[0]?.size ?? 0, false)}
                    </p>
                  </div>
                </div>
                <Button
                  className='text-red-500 bg-[#F9E0E0] h-8 px-2 '
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onImageRemove(0);
                  }}
                >
                  <X size={18} className='mr-2' color='red' />
                  {intl.formatMessage(fileUploadMessages.removeAction)}
                  <span className='sr-only'>
                    {intl.formatMessage(fileUploadMessages.removeSrOnly)}
                  </span>
                </Button>
              </div>
            );
          }

          //Show File Input
          return (
            <FileInput
              onImageUpload={onImageUpload}
              className={className}
              disabled={disabled}
              {...dragProps}
            />
          );
        }}
      </ImageUploading>
    </div>
  );
};

/**
 * Props for the FileUploaderComponent
 * @template TFieldValues Field values used in the form
 * @template TName The field name from TFieldValues
 */
type FileUploaderProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = {
  field: ControllerRenderProps<TFieldValues, TName>;
  formState: UseFormStateReturn<TFieldValues>;
  className?: string;
  isMultiple?: boolean;
  maxFiles?: number;
  onRemove?: (url: string) => void;
  labelsProps?: {
    label?: string;
    labelClassName?: string;
    isRequired?: boolean;
    showLabel?: boolean;
  };
  placeholder?: string;
  maxFilePreview?: number;
  isForFileUpload?: boolean;
  acceptFileTypes?: string[]; //Uploadable file types Note: Don't use "." just the file extension eg. ["pdf", "docx"] don't use [".pdf", ".docx"]
  allowVideoUpload?: boolean;
  fileLimit?: number;
  excludeFileLimit?: boolean;
  uploadedFilesFromParents?: File[];
  uploadedFilesBatchKey?: number;
  existingFileSizes?: number[];
  disabled?: boolean;
};

export const FileUploader = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(
  props: FileUploaderProps<TFieldValues, TName>,
): JSX.Element => {
  const {
    field,
    labelsProps,
    formState,
    disabled,
    uploadedFilesFromParents,
    existingFileSizes,
  } = props;
  const {
    images,
    setImages,
    setIsForFileUpload,
    setAcceptFileTypes,
    setIsAllowVideoUpload,
  } = useFileContext();
  const { onChange: updateFieldValue, value: fieldValue } = field;
  const fieldValueRef = useRef(fieldValue);
  const updateFieldValueRef = useRef(updateFieldValue);
  const isSyncingDefaultImagesRef = useRef(false);
  fieldValueRef.current = fieldValue;
  updateFieldValueRef.current = updateFieldValue;

  //Save original images
  const originalDefaultValues =
    formState?.defaultValues &&
    (formState?.defaultValues[field.name] as string[] | File[]);

  const randomSizeFallback = useMemo(() => {
    return (
      Math.floor(Math.random() * (1.7 * 1024 * 1024 - 1 * 1024 * 1024 + 1)) +
      1 * 1024 * 1024
    );
  }, []);

  //Handle Form Reset or Discard
  const handleFormReset = useCallback((): void => {
    if (originalDefaultValues) {
      const updateImages = originalDefaultValues.map(
        (value: string | File, index: number) => {
          if (typeof value === "string" && isHttps(value)) {
            return {
              dataURL: value,
              name: splitImageFileName(value, imgBaseUrl),
              size: existingFileSizes?.[index] ?? randomSizeFallback,
            };
          }

          return {
            file: value as File,
            name: (value as File)?.name,
            size: (value as File)?.size,
          };
        },
      );

      isSyncingDefaultImagesRef.current = true;
      setImages(updateImages as ExtendedImageListType);

      return;
    }
    setImages([]);
  }, [existingFileSizes, originalDefaultValues, randomSizeFallback, setImages]);

  // Sync Form default values to File Context (runs only on initial load)
  useEffect(() => {
    if (field.value === null || field.value === undefined) {
      return;
    }
    if (field.value && images.length === 0) {
      const updateImages = field.value?.map(
        (value: string | File, index: number) => {
          //handle Values being Null
          if (!value) {
            return {
              dataURL: undefined,
              file: undefined,
              name: undefined,
              size: undefined,
            };
          }
          if (typeof value === "string" && isHttps(value)) {
            return {
              dataURL: value,
              name: splitImageFileName(value, imgBaseUrl),
              size: existingFileSizes?.[index] ?? randomSizeFallback,
            };
          }

          return {
            file: value as File,
            name: (value as File)?.name,
            size: (value as File)?.size,
          };
        },
      );

      isSyncingDefaultImagesRef.current = true;
      setImages(updateImages as ExtendedImageListType);
    }
  }, [
    existingFileSizes,
    field.value,
    images.length,
    randomSizeFallback,
    setImages,
  ]);

  const isDefaultValueRestored = useMemo(
    () => JSON.stringify(originalDefaultValues) === JSON.stringify(field.value),
    [field.value, originalDefaultValues],
  );

  // Sync Form default values to File Context (runs only on initial load)
  useEffect(() => {
    // If the form is reset or discarded
    // then update the images to the original default values
    if (isDefaultValueRestored) {
      handleFormReset();
    }
  }, [handleFormReset, isDefaultValueRestored]);

  useEffect(() => {
    if (images.length > 0) {
      if (
        (images[0].dataURL === undefined && images[0]?.file === undefined) ||
        images[0].file === null
      ) {
        updateFieldValueRef.current([]);
        setImages([]);

        return;
      }
      const updatedFieldValues = images.map((image) =>
        image?.dataURL && isHttps(image.dataURL) ? image?.dataURL : image?.file,
      );

      const isDefaultImageSync =
        isSyncingDefaultImagesRef.current &&
        areUploadFieldValuesEqual(fieldValueRef.current, updatedFieldValues);
      isSyncingDefaultImagesRef.current = false;

      if (isDefaultImageSync) {
        return;
      }

      updateFieldValueRef.current(updatedFieldValues);
    }
  }, [images, setImages]);

  useEffect(() => {
    setIsForFileUpload(Boolean(props.isForFileUpload));
    setAcceptFileTypes(props.acceptFileTypes ?? []);
    setIsAllowVideoUpload(Boolean(props.allowVideoUpload));
  }, [
    props.acceptFileTypes,
    props.allowVideoUpload,
    props.isForFileUpload,
    setAcceptFileTypes,
    setIsAllowVideoUpload,
    setIsForFileUpload,
  ]);

  return (
    <FormWrapper
      label={!labelsProps?.showLabel ? "" : labelsProps?.label}
      isRequired={labelsProps?.isRequired}
      className={cn(
        labelsProps?.labelClassName,
        !labelsProps?.showLabel ? "space-y-0" : "",
      )}
    >
      <FileInputWrapper
        isMultiple={props.isMultiple}
        maxFiles={props.maxFiles}
        className={props.className}
        onRemove={props.onRemove}
        maxFilePreview={props.maxFilePreview}
        filesUploadedFromParent={uploadedFilesFromParents}
        filesUploadedBatchKey={props.uploadedFilesBatchKey}
        disabled={disabled}
      />
    </FormWrapper>
  );
};

export const FileUploaderComponent = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(
  props: FileUploaderProps<TFieldValues, TName>,
): JSX.Element => {
  return (
    <FileProvider
      excludeFileLimit={props?.excludeFileLimit}
      fileLimit={props?.fileLimit}
    >
      <FileUploader {...props} />
    </FileProvider>
  );
};

FileUploaderComponent.displayName = "FileUploaderComponent";
